import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useState } from "react";
import { isVersionBelowMinimum } from "./semver";

interface ForceUpgradeState {
  checking: boolean;
  blocked: boolean;
  nudge: boolean;
  storeUrl: string;
  isWeb: boolean;
  dismissNudge: () => void;
}

function isNudgeDismissed(recommendedVersion: string): boolean {
  try {
    return (
      localStorage.getItem(`upgradeNudgeDismissed_${recommendedVersion}`) ===
      "true"
    );
  } catch {
    return false;
  }
}

function persistNudgeDismissal(recommendedVersion: string): void {
  try {
    localStorage.setItem(`upgradeNudgeDismissed_${recommendedVersion}`, "true");
  } catch {
    // Ignore storage errors
  }
}

export function useForceUpgrade(): ForceUpgradeState {
  const platform = Capacitor.getPlatform();
  const isWeb = platform === "web";

  const [state, setState] = useState<
    Omit<ForceUpgradeState, "dismissNudge" | "isWeb">
  >({
    checking: true,
    blocked: false,
    nudge: false,
    storeUrl: "",
  });

  const [recommendedVersion, setRecommendedVersion] = useState("0.0.0");

  const dismissNudge = useCallback(() => {
    persistNudgeDismissal(recommendedVersion);
    setState((prev) => ({ ...prev, nudge: false }));
  }, [recommendedVersion]);

  useEffect(() => {
    const mappedPlatform = isWeb
      ? "web"
      : platform === "ios"
      ? "ios"
      : "android";

    let cancelled = false;

    async function check() {
      try {
        const currentVersion = isWeb
          ? __APP_VERSION__
          : (await CapacitorApp.getInfo()).version;

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        const apiBase = import.meta.env.VITE_API_ENDPOINT;
        const res = await fetch(
          `${apiBase}/v1/appVersion?platform=${mappedPlatform}`,
          { signal: controller.signal }
        );
        clearTimeout(timeout);

        if (!res.ok) {
          if (!cancelled)
            setState({
              checking: false,
              blocked: false,
              nudge: false,
              storeUrl: "",
            });
          return;
        }

        const data = await res.json();
        const { minimumVersion, storeUrl } = data as {
          minimumVersion: string;
          recommendedVersion?: string;
          storeUrl: string;
        };
        const recVersion =
          (data as { recommendedVersion?: string }).recommendedVersion ??
          "0.0.0";

        if (!cancelled) {
          setRecommendedVersion(recVersion);

          if (isVersionBelowMinimum(currentVersion, minimumVersion)) {
            setState({
              checking: false,
              blocked: true,
              nudge: false,
              storeUrl,
            });
          } else if (
            isVersionBelowMinimum(currentVersion, recVersion) &&
            !isNudgeDismissed(recVersion)
          ) {
            setState({
              checking: false,
              blocked: false,
              nudge: true,
              storeUrl,
            });
          } else {
            setState({
              checking: false,
              blocked: false,
              nudge: false,
              storeUrl: "",
            });
          }
        }
      } catch {
        // Fail open on any error
        if (!cancelled)
          setState({
            checking: false,
            blocked: false,
            nudge: false,
            storeUrl: "",
          });
      }
    }

    void check();
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...state, isWeb, dismissNudge };
}
